import {useQuery} from '@apollo/client';
import {Alert, Button, Select, Table, Tag, Tooltip, Typography} from 'antd';
import moment from 'moment';
import {useState} from 'react';
import {GET_MANUAL_KYC_QUEUE} from 'graphql/queries/users';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  buildQueueCsv,
  confirmQueueExport,
  humanizeEnum,
  kycStatusLabel,
  useCanExportKyc,
  kycTabHref,
  KycUserLink,
  renderKycStatusTag,
} from './kycQueueShared';
import {
  EMPTY_CHECK_FILTERS,
  EMPTY_QUEUE_FILTERS,
  KycCheckFilters,
  KycCountryFilter,
  KycQueueFilterSummary,
  KycQueueFilters,
  QueueFilterField,
  buildCheckFilterInput,
  buildKycFilterInput,
} from './kycQueueFilters';
import styles from './kycDashboard.module.scss';
import {useKycNotify} from './kycNotifyAction';
import {ExportButton} from 'components/button/ExportButton/ExportButton';

const SMILE_STATUS_OPTIONS = [
  {value: 'NOT_SUBMITTED', label: 'Not sent to Smile'},
  {value: 'SUBMITTING', label: 'Sending'},
  {value: 'SUBMITTED', label: 'Sent, awaiting verdict'},
  {value: 'VERDICT_RECEIVED', label: 'Smile answered'},
  {value: 'FAILED', label: 'Send failed'},
  {value: 'INELIGIBLE', label: 'Cannot be sent'},
];

// What Smile actually said, once it has said it. `clear` is the only outcome
// that decides anything on its own; the rest are handed back to a reviewer.
const VERDICT_TAG = {
  clear: {color: 'green', label: 'Smile: cleared'},
  attention: {color: 'gold', label: 'Smile: needs review'},
};

const SMILE_STATUS_TAG = {
  NOT_SUBMITTED: {color: 'default', label: 'Not sent'},
  SUBMITTING: {color: 'processing', label: 'Sending'},
  SUBMITTED: {color: 'blue', label: 'Awaiting verdict'},
  VERDICT_RECEIVED: {color: 'green', label: 'Smile answered'},
  FAILED: {color: 'red', label: 'Send failed'},
  INELIGIBLE: {color: 'orange', label: 'Cannot be sent'},
};

// A verdict outranks the status. Cards answered before VERDICT_RECEIVED existed
// still carry status SUBMITTED, and reading that literally would show "awaiting
// verdict" next to a user Smile has already cleared.
export const describeSmileSubmission = submission => {
  const outcome = submission?.verdict?.outcome;
  if (outcome) {
    return (
      VERDICT_TAG[outcome] || {
        color: 'gold',
        label: `Smile: ${outcome.replace(/_/g, ' ')}`,
      }
    );
  }
  const status = submission?.status || 'NOT_SUBMITTED';
  return SMILE_STATUS_TAG[status] || {color: 'default', label: status};
};

const fullNameOf = user =>
  `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

const documentTypeOf = record =>
  humanizeEnum(record?.providerDocumentType || record?.documentType);

const submittedAtOf = record =>
  record?.kycInitiatedAt
    ? moment(record.kycInitiatedAt).format('DD MMM YYYY, HH:mm')
    : '-';

const {Title, Text} = Typography;

export const ManualKycQueueTab = () => {
  const {canNotify, openKycNotify, kycNotifyModals} = useKycNotify();
  const canExport = useCanExportKyc();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // The tab still opens on the awaiting-review queue, but as a value in the
  // identity-check list rather than a separate argument — so picking All
  // identity statuses genuinely clears it and an AML-only search is not still
  // pinned to awaiting-review cards.
  const [checks, setChecks] = useState({
    ...EMPTY_CHECK_FILTERS,
    kycStatuses: ['PENDING_VERIFICATION'],
  });
  const [smileStatus, setSmileStatus] = useState(undefined);
  const [filters, setFilters] = useState(EMPTY_QUEUE_FILTERS);

  // Any filter change renumbers the results, so never keep the old page.
  const applyFilter = setter => value => {
    setter(value);
    setPage(1);
  };

  // The status selectors each send a patch for their own field.
  const applyChecks = patch => {
    setChecks(current => ({...current, ...patch}));
    setPage(1);
  };

  // Country patches from the row, and whole objects from the modal and the
  // summary's tags — merging covers both without either clobbering the other.
  const applyFilters = patch => {
    setFilters(current => ({...current, ...patch}));
    setPage(1);
  };

  // errorPolicy 'all' so an unpublished gateway schema shows an empty queue
  // rather than taking the whole KYC page down.
  const {data, loading, error, refetch} = useQuery(GET_MANUAL_KYC_QUEUE, {
    variables: {
      page,
      limit: pageSize,
      smileSubmissionStatus: smileStatus,
      filters: {
        ...buildKycFilterInput(filters),
        ...buildCheckFilterInput(checks),
      },
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const payload = data?.getKycVerificationRequests;
  // The field is a union: the Error branch carries a message instead of rows.
  const queueError =
    payload?.__typename === 'Error' ? payload.message : error?.message;
  const requests = payload?.requests || [];
  const totalDocs = payload?.totalDocs || 0;

  const handleTableChange = pagination => {
    if (pagination?.pageSize && pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      // A new page size renumbers the pages, so go back to the first one
      // rather than landing past the end of a shorter list.
      setPage(1);
      return;
    }
    setPage(pagination?.current || 1);
  };

  const columns = [
    {
      title: 'Full Name',
      key: 'fullName',
      // Pinned: the row is wider than the screen, and scrolling out to the
      // Smile ID or AML column otherwise leaves a row of statuses with nothing
      // saying whose they are. A fixed column needs an explicit width.
      fixed: 'left',
      width: 180,
      render: (_, record) => (
        <KycUserLink user={record?.user}>
          {fullNameOf(record?.user)}
        </KycUserLink>
      ),
      exportValue: record => fullNameOf(record?.user),
    },
    {
      title: 'Email',
      key: 'email',
      render: (_, record) => (
        <KycUserLink user={record?.user}>{record?.user?.email}</KycUserLink>
      ),
      exportValue: record => record?.user?.email || '-',
    },
    {
      title: 'Country',
      key: 'country',
      render: (_, record) => record?.user?.countryISO || '-',
      exportValue: record => record?.user?.countryISO || '-',
    },
    {
      title: 'Document Type',
      key: 'documentType',
      render: (_, record) => documentTypeOf(record),
      exportValue: documentTypeOf,
    },
    {
      title: 'Submitted At',
      key: 'kycInitiatedAt',
      render: (_, record) => submittedAtOf(record),
      exportValue: submittedAtOf,
    },
    {
      title: 'KYC Status',
      key: 'kycStatus',
      render: (_, record) => renderKycStatusTag(record?.kycStatus),
      exportValue: record => kycStatusLabel(record?.kycStatus),
    },
    // The other two checks are filterable here now, so they are also shown —
    // a reviewer filtering on AML and seeing no AML column has no way to tell
    // a filtered queue from a broken one.
    {
      title: 'Document Check',
      key: 'documentCheckStatus',
      render: (_, record) => renderKycStatusTag(record?.documentCheckStatus),
      exportValue: record => kycStatusLabel(record?.documentCheckStatus),
    },
    {
      title: 'AML',
      key: 'amlStatus',
      render: (_, record) => renderKycStatusTag(record?.amlStatus),
      exportValue: record => kycStatusLabel(record?.amlStatus),
    },
    {
      title: 'Smile ID',
      key: 'smileSubmission',
      exportValue: record =>
        describeSmileSubmission(record?.smileSubmission).label,
      render: (_, record) => {
        const submission = record?.smileSubmission;
        const tag = describeSmileSubmission(submission);
        // The detail that decides what a reviewer does next: why it failed, or
        // what Smile actually answered.
        const hint = [
          submission?.verdict?.outcome
            ? `Verdict: ${submission.verdict.outcome}${
                submission.verdict.resultCode
                  ? ` (${submission.verdict.resultCode})`
                  : ''
              }`
            : null,
          submission?.lastError ? `Last error: ${submission.lastError}` : null,
          submission?.submittedAt
            ? `Sent ${moment(submission.submittedAt).format(
                'DD MMM YYYY, HH:mm',
              )}${
                submission?.triggeredBy === 'JOB'
                  ? ' by the background job'
                  : submission?.triggeredBy === 'ADMIN'
                  ? ' by an admin'
                  : ''
              }`
            : null,
        ]
          .filter(Boolean)
          .join(' · ');

        return hint ? (
          <Tooltip title={hint}>
            <Tag color={tag.color}>{tag.label}</Tag>
          </Tooltip>
        ) : (
          <Tag color={tag.color}>{tag.label}</Tag>
        );
      },
    },
    {
      title: '',
      key: 'action',
      // Pinned to the other edge for the same reason: Review and Notify stay
      // reachable without scrolling back across every status column.
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        if (!record?.user?.id) return null;
        return (
          <div className={styles.queueRowActions}>
            {/* An anchor rather than a navigate(): the reviewer works down the
                queue, so opening each submission in its own tab keeps this
                list, its filters and its page in place. It also restores
                middle-click, cmd-click and "copy link address", which a
                button-with-onClick silently swallows. */}
            <a
              className={styles.queueReviewLink}
              href={kycTabHref(record.user.id)}
              target="_blank"
              rel="noopener noreferrer">
              Review
            </a>
            {canNotify && (
              <button
                type="button"
                className={styles.queueNotifyButton}
                onClick={() => openKycNotify(record.user)}>
                Notify
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const csv = buildQueueCsv(columns, requests);

  return (
    <div className={styles.container}>
      <div className={styles.sectionIntro}>
        <Title level={4}>Manual Review Queue</Title>
        <Text type="secondary">
          Users who uploaded their documents through the manual fallback. Open a
          row to inspect the images and set the KYC status. Deciding a
          submission does not delete it — it moves to that status, so switch the
          filter to see approved or rejected ones.
        </Text>
      </div>

      {queueError && (
        <Alert
          type="error"
          showIcon
          className={styles.queueError}
          message="The queue could not be loaded"
          description={`${queueError} — an empty table below does not mean there is nothing to review.`}
        />
      )}

      <div className={styles.queueActions}>
        <KycCheckFilters value={checks} onChange={applyChecks} />
        <QueueFilterField label="Smile ID" htmlFor="manual-smile-status">
          <Select
            id="manual-smile-status"
            value={smileStatus}
            onChange={applyFilter(setSmileStatus)}
            options={SMILE_STATUS_OPTIONS}
            allowClear
            placeholder="Any status"
          />
        </QueueFilterField>
        <KycCountryFilter value={filters} onChange={applyFilters} />
        <div className={styles.queueActionsEnd}>
          {/* The page on screen, not the whole filtered queue — there is no
              server-side export yet and the browser only holds this page. */}
          {canExport && (
            <ExportButton
              csvHeaders={csv.headers}
              csvData={csv.data}
              fileName={`manual-kyc-queue-page-${page}.csv`}
              label="Export"
              asyncOnClick
              onClick={confirmQueueExport(csv.data.length)}
            />
          )}
          <KycQueueFilters value={filters} onChange={applyFilters} />
          <Button onClick={() => refetch()} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <KycQueueFilterSummary value={filters} onChange={applyFilters} />

      <Table
        columns={columns}
        dataSource={requests}
        loading={loading}
        className="antd-table"
        onChange={handleTableChange}
        scroll={{x: 'max-content'}}
        pagination={{
          current: page,
          pageSize,
          total: totalDocs,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: total =>
            `${total} ${
              checks.kycStatuses.length === 1 &&
              checks.kycStatuses[0] === 'PENDING_VERIFICATION'
                ? 'awaiting review'
                : 'matching'
            }`,
        }}
        rowKey={(record, index) => record?.id || index}
      />
      {kycNotifyModals}
    </div>
  );
};
